import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListAllowLists } from 'src/features/parkingManagement/api/AllowListAPI/useListAllowLists';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import CreateAllowListFormModal from 'src/features/parkingManagement/components/AllowLists/forms/CreateAllowListFormModal';
import CellServiceProviderActions from 'src/features/parkingManagement/components/AllowLists/table/CellAllowListActions';
import {
    ALLOW_LIST_PURPOSE_TRANSLATION_MAP,
    allowListTypeBadgeOptions,
} from 'src/features/parkingManagement/types/allowList';
import { allowListMessages } from 'src/features/parkingManagement/utils/allowListMessages';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages } from 'src/utils/messages';

import type { AllowList } from 'src/features/parkingManagement/types/allowList';

const columnHelper = createColumnHelper<AllowList>();

export default function AllowListsTable() {
    const { parkingId = '' } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch();

    const { data, isLoading, isError, refetch } = useListAllowLists({
        ...queryPaginationOptions,
        ...querySearchOptions,
        parkingId,
        config: { enabled: !!parkingId },
    });
    const { data: parking } = useGetParking({ parkingId, config: { enabled: !!parkingId } });

    React.useEffect(() => {
        if (!parking) return;
        setTitle(parking.name);
        return () => removeTitle();
    }, [parking, formatMessage]);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                badgeOptions: allowListTypeBadgeOptions,
                title: formatMessage(allowListMessages.type),
            },
        }),
        columnHelper.accessor('purposes', {
            header: DataTableColumnHeader,
            cell: (props) =>
                props.row.original.purposes
                    .map((purpose) => formatMessage(ALLOW_LIST_PURPOSE_TRANSLATION_MAP[purpose]))
                    .join(', '),
            meta: {
                title: formatMessage(allowListMessages.purposes),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellServiceProviderActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            pagination={pagination}
            search={search}
            semantics={{
                title: formatMessage(allowListMessages.allowLists),
                entryName: formatMessage(allowListMessages.allowList).toLowerCase(),
                entryNames: formatMessage(allowListMessages.allowLists).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            refetch={refetch}
            components={{
                newEntry: CreateAllowListFormModal,
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
}
